class Accordion {
    constructor() {
        // Store DOM elements
        this.accordions = document.querySelectorAll('.c-accordion');
        this.propertyFilterReveal = document.querySelector('.cc-property_filter_reveal');
        
        // Bind methods
        this.handleClick = this.handleClick.bind(this);
        this.handleFilterReveal = this.handleFilterReveal.bind(this);
        
        // Track active animations
        this.activeTimelines = new Map();
        
        // Initialize
        this.init();
    }

    init() {
        // Add click listeners to all accordion buttons
        this.accordions.forEach(accordion => {
            const button = accordion.querySelector('.c-accordion_btn');
            const content = accordion.querySelector('.c-accordion_content');
            
            // Set initial ARIA states
            button.setAttribute('aria-expanded', 'false');
            content.setAttribute('hidden', '');
            
            // Add click listener
            button.addEventListener('click', () => this.handleClick(accordion));
        });

        // Add click listener for property filter reveal button
        if (this.propertyFilterReveal) {
            this.propertyFilterReveal.addEventListener('click', this.handleFilterReveal);
        }
    }

    handleClick(accordion) {
        const button = accordion.querySelector('.c-accordion_btn');
        const content = accordion.querySelector('.c-accordion_content');
        const listItems = content.querySelectorAll('.c-accordion_content_list > li');
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const isLeaderAccordion = accordion.querySelector('.c-leader_content') !== null;

        // If this is a leader accordion being opened, close other open leader accordions
        if (!isExpanded && isLeaderAccordion) {
            this.accordions.forEach(otherAccordion => {
                if (otherAccordion !== accordion && otherAccordion.querySelector('.c-leader_content')) {
                    const otherButton = otherAccordion.querySelector('.c-accordion_btn');
                    const otherContent = otherAccordion.querySelector('.c-accordion_content');
                    const otherListItems = otherContent.querySelectorAll('.c-accordion_content_list > li');
                    const isOtherExpanded = otherButton.getAttribute('aria-expanded') === 'true';

                    if (isOtherExpanded) {
                        // Kill any active timeline for this accordion
                        if (this.activeTimelines.has(otherAccordion)) {
                            const activeTimeline = this.activeTimelines.get(otherAccordion);
                            activeTimeline.kill();
                            this.activeTimelines.delete(otherAccordion);
                        }

                        // Update state
                        otherButton.setAttribute('aria-expanded', 'false');
                        otherAccordion.classList.remove('active');
                        
                        // Collapse the accordion
                        this.collapseAccordion(otherAccordion, otherContent, otherListItems);
                    }
                }
            });
        }

        // If this is the map accordion being opened, dispatch an event to close the filter accordion
        if (!isExpanded && accordion.closest('.c-col_map')) {
            // Dispatch a custom event that PropertyAjax can listen for
            const event = new CustomEvent('mapAccordionOpened', {
                detail: { accordion }
            });
            document.dispatchEvent(event);
        }

        // Kill any active timeline for this accordion
        if (this.activeTimelines.has(accordion)) {
            const activeTimeline = this.activeTimelines.get(accordion);
            activeTimeline.kill();
            this.activeTimelines.delete(accordion);
        }

        // Toggle ARIA attributes
        button.setAttribute('aria-expanded', !isExpanded);
        
        // Toggle active class on accordion container
        accordion.classList.toggle('active');
        
        if (isExpanded) {
            // Collapse animation
            this.collapseAccordion(accordion, content, listItems);
        } else {
            // Expand animation
            content.removeAttribute('hidden');
            this.expandAccordion(accordion, content, listItems);
        }
    }

    expandAccordion(accordion, content, listItems) {
        // First, set height to auto and get the natural height
        gsap.set(content, { height: 'auto' });
        const height = content.offsetHeight;
        const leaderContent = accordion.querySelector('.c-leader_content');
        const leaderImage = accordion.querySelector('.c-leader_image');
        const leaderDescription = accordion.querySelector('.c-leader_description');
        const animateItems = accordion.querySelectorAll('[data-animate]');

        if (animateItems.length) {
            // set all animate items to y: 30, opacity: 0, visibility: 'visible'
            gsap.set(animateItems, { 
                y: 30,
                opacity: 0,
                visibility: 'visible'
            });

            // add a scroll trigger for each animate item
            animateItems.forEach(item => {
                ScrollTrigger.create({
                    trigger: item,
                    start: 'top 80%',
                    end: 'bottom bottom',
                    animation: gsap.to(item, {
                        y: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power2.out'
                    })
                });
            });
        }
        
        // Set initial states for elements that exist
        if (leaderImage) {
            gsap.set(leaderImage, { 
                y: 30,
                opacity: 0,
                visibility: 'visible'
            });
        }
        if (leaderDescription) {
            gsap.set(leaderDescription, { 
                y: 30,
                opacity: 0,
                visibility: 'visible'
            });
        }
        
        // Set back to 0 immediately
        gsap.set(content, { height: 0 });
        
        // Create timeline for animations
        const tl = gsap.timeline({
            defaults: { ease: 'power2.out' },
            onComplete: () => {
                this.activeTimelines.delete(accordion);
                gsap.set(content, { height: 'auto' });
                // The ResizeObserver in LeadersMasonry will handle layout updates
            }
        });

        // Store the timeline reference
        this.activeTimelines.set(accordion, tl);

        // Animate the container height first
        if (content) {
            tl.to(content, {
                height: height,
                duration: 0.5
            });
        }

        // Animate list items if they exist (for non-leader accordions)
        if (listItems.length) {
            gsap.set(listItems, { 
                y: 20,
                opacity: 0 
            });

            tl.to(listItems, {
                y: 0,
                opacity: 1,
                duration: 1,
                stagger: 0.05
            }, '-=0.3');
        }

        // Animate headshot if it exists
        if (leaderImage) {
            tl.to(leaderImage, {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out"
            }, '-=0.3');
        }

        // Animate description if it exists
        if (leaderDescription) {
            tl.to(leaderDescription, {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out"
            }, '-=0.3');
        }
    }

    collapseAccordion(accordion, content, listItems) {
        const tl = gsap.timeline({
            defaults: { ease: 'power2.inOut' },
            onComplete: () => {
                content.setAttribute('hidden', '');
                this.activeTimelines.delete(accordion);
                // The ResizeObserver in LeadersMasonry will handle layout updates
            }
        });

        // Store the timeline reference
        this.activeTimelines.set(accordion, tl);

        const leaderImage = accordion.querySelector('.c-leader_image');
        const leaderDescription = accordion.querySelector('.c-leader_description');
        const animateItems = accordion.querySelectorAll('[data-animate]');

        // add animation to timeline for all animate items to animate at once
        // destroy the timeline after it completes
        if (animateItems.length) {
            tl.to(animateItems, {
                y: -30,
                opacity: 0,
                duration: 0.4,
                ease: "power2.inOut"
            }, '');
        }

        if (listItems.length) {
            tl.to(listItems, {
                y: -20,
                opacity: 0,
                duration: 0.4
            });
        }

        // Animate description out first if it exists
        if (leaderDescription) {
            tl.to(leaderDescription, {
                y: -30,
                opacity: 0,
                duration: 0.4,
                ease: "power2.inOut"
            });
        }

        // Animate headshot out if it exists
        if (leaderImage) {
            tl.to(leaderImage, {
                y: -30,
                opacity: 0,
                duration: 0.4,
                ease: "power2.inOut"
            }, '-=0.2');
        }

        // Then animate the container height
        tl.to(content, {
            height: 0,
            duration: 0.4
        }, '-=0.2');
    }

    handleFilterReveal() {
        // Find the map accordion
        const mapAccordion = document.querySelector('.c-col_map .c-accordion');
        if (!mapAccordion) return;

        const mapButton = mapAccordion.querySelector('.c-accordion_btn');
        const mapContent = mapAccordion.querySelector('.c-accordion_content');
        const mapListItems = mapContent.querySelectorAll('.c-accordion_content_list > li');
        const isMapExpanded = mapButton.getAttribute('aria-expanded') === 'true';

        if (isMapExpanded) {
            // Kill any active timeline for the map accordion
            if (this.activeTimelines.has(mapAccordion)) {
                const activeTimeline = this.activeTimelines.get(mapAccordion);
                activeTimeline.kill();
                this.activeTimelines.delete(mapAccordion);
            }

            // Update state immediately
            mapButton.setAttribute('aria-expanded', 'false');
            mapAccordion.classList.remove('active');
            
            // Force content to be hidden after animation
            const collapseTimeline = gsap.timeline({
                onComplete: () => {
                    mapContent.setAttribute('hidden', '');
                    if (this.activeTimelines.has(mapAccordion)) {
                        this.activeTimelines.delete(mapAccordion);
                    }
                }
            });

            // Store the timeline reference
            this.activeTimelines.set(mapAccordion, collapseTimeline);

            // Animate the collapse
            collapseTimeline.to(mapContent, {
                height: 0,
                duration: 0.4,
                ease: "power2.inOut"
            });
        }
    }

    destroy() {
        // Kill all active animations
        this.activeTimelines.forEach(timeline => timeline.kill());
        this.activeTimelines.clear();

        // Remove event listeners
        this.accordions.forEach(accordion => {
            const button = accordion.querySelector('.c-accordion_btn');
            button.removeEventListener('click', this.handleClick);
        });
        
        if (this.propertyFilterReveal) {
            this.propertyFilterReveal.removeEventListener('click', this.handleFilterReveal);
        }
    }
}

export default Accordion;
